import { useMutation, useQuery, useQueryClient } from "@/lib/query";
import { useNavigate } from "react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { adminApi, adminKeys, pendingDoctorsQuery } from "@/lib/api/adminApi";
import { ApiError } from "@/lib/api/client";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import type { Doctor } from "@/types/doctor";

/** Maps documented admin error statuses onto the portal's UX rules. */
export function useAdminErrorHandler() {
  const { signOut } = useAdminAuth();
  const navigate = useNavigate();

  return useCallback(
    (error: unknown) => {
      const status = error instanceof ApiError ? error.status : 0;
      if (status === 403) {
        signOut();
        toast.error("Invalid Administrator Secret");
        navigate("/admin/login", { replace: true });
        return;
      }
      if (status === 503) {
        toast.error("Admin tools are currently disabled.");
        return;
      }
      if (status === 404) {
        toast.error("Doctor not found. The list may be out of date.");
        return;
      }
      toast.error(
        error instanceof Error ? error.message : "Something went wrong. Please try again.",
      );
    },
    [signOut, navigate],
  );
}

export function usePendingDoctors() {
  const { secret } = useAdminAuth();
  const handleError = useAdminErrorHandler();
  const [refreshing, setRefreshing] = useState(false);

  const query = useQuery(pendingDoctorsQuery(secret));
  const queryError = query.error;

  useEffect(() => {
    if (queryError) handleError(queryError);
  }, [queryError, handleError]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const result = await query.refetch();
      if (result.error) {
        handleError(result.error);
        return;
      }
      toast.success("Pending doctors refreshed");
    } finally {
      setRefreshing(false);
    }
  }, [query, handleError]);

  return {
    doctors: query.data?.doctors ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refresh,
    refreshing: refreshing || query.isFetching,
  };
}

export function useDoctorDecisions(onSettled?: () => void) {
  const { secret, recordAction } = useAdminAuth();
  const queryClient = useQueryClient();
  const handleError = useAdminErrorHandler();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: adminKeys.pending });

  const approve = useMutation({
    mutationFn: (doctor: Doctor) => adminApi.approveDoctor(secret ?? "", doctor.id),
    onSuccess: (_data, doctor) => {
      recordAction({
        id: `${doctor.id}-approved-${Date.now()}`,
        doctorId: doctor.id,
        doctorName: doctor.name,
        specialization: doctor.specialization,
        kind: "approved",
        at: new Date().toISOString(),
      });
      toast.success(`${doctor.name} approved`, {
        description: "The doctor is now visible in the HealNova patient application.",
      });
      void invalidate();
      onSettled?.();
    },
    onError: handleError,
  });

  const reject = useMutation({
    mutationFn: ({ doctor, reason }: { doctor: Doctor; reason?: string }) =>
      adminApi.rejectDoctor(secret ?? "", doctor.id, reason),
    onSuccess: (_data, { doctor, reason }) => {
      recordAction({
        id: `${doctor.id}-rejected-${Date.now()}`,
        doctorId: doctor.id,
        doctorName: doctor.name,
        specialization: doctor.specialization,
        kind: "rejected",
        at: new Date().toISOString(),
        reason: reason?.trim() ? reason.trim() : undefined,
      });
      toast.success(`${doctor.name} rejected`);
      void invalidate();
      onSettled?.();
    },
    onError: handleError,
  });

  return { approve, reject };
}
